import { Renderer } from './engine/renderer.js';
import { Cathedral } from './scene/cathedral.js';
import { Fractal } from './scene/fractal.js';
import { ModeSwitcher } from './scene/mode-switcher.js';
import { RenderLoop } from './engine/loop.js';
import { AdaptiveScaler } from './engine/adaptive.js';
// Vite hashes the asset filename in prod; importing as ?url is the only
// reliable way to get a stable URL for the bundled audio file. File is a
// 16-bit mono 44.1kHz WAV (was misnamed .mp3 in the prototype, which made
// some browsers decode garbage and produce a hum on playback).
import demoUrl from './assets/cathedral-demo.wav?url';
import { AudioEngine } from './audio/engine.js';
import { SourceManager } from './audio/source.js';
import { SourcePicker } from './ui/source-picker.js';
import { AudioDebugOverlay } from './ui/audio-debug.js';
import { KnobPanel } from './ui/knob-panel.js';
import { MenuSheet, collapsibleSection, injectSectionStyle } from './ui/menu-sheet.js';
import { readUrlState, writeUrlState } from './state.js';
// Digable Planets: mobile/touch responsive overrides on top of the
// inherited cathedral desktop styles. Additive @media rules only.
import './ui/responsive.css';

const canvas = document.getElementById('canvas');
const fpsEl = document.getElementById('fps');

const renderer = new Renderer(canvas);
// Static bloom — the background was strobing on every beat which read as
// "flashing". Frozen at sensible glow defaults; audio reactivity now lives
// in the fractal itself (color/morphology/kaleidoscope) instead.
//
// Cold-start ramp: bloom mips are uninitialized on frame 1, which stalls the
// GPU pipeline if we hit it with full strength right away. Start dim and
// lerp up over BLOOM_RAMP_FRAMES so the brightpass + blur kernel warms in.
const BLOOM_TARGET_STRENGTH = 0.7;
const BLOOM_START_STRENGTH = 0.2;
const BLOOM_RAMP_FRAMES = 30;
renderer.setBloom({ strength: BLOOM_START_STRENGTH, radius: 0.6, threshold: 0.85 });

// Build both visual modes; ModeSwitcher mounts the active one.
// Cathedral stays available behind the C hotkey for M4/M5 reference.
const cathedral = new Cathedral();
const initialState = readUrlState();
const fractal = new Fractal({
  initialPaletteId: initialState.palette,
  initialFractalId: initialState.fractal,
  initialView: initialState.view,
});
const modeSwitcher = new ModeSwitcher({
  fractal,
  cathedral,
  parentScene: renderer.scene,
  onStateChange: (s) => {
    writeUrlState(s);
    knobPanel?._refreshSoundboard?.();
  },
  onModeChange: () => {
    knobPanel?._refreshSoundboard?.();
  },
});
// Reflect initial state into URL so shared links normalize/round-trip.
writeUrlState({
  palette: initialState.palette,
  fractal: initialState.fractal,
  view: initialState.view,
});

const audioEngine = new AudioEngine();
const sourceManager = new SourceManager({ engine: audioEngine, demoUrl });

const debugOverlay = new AudioDebugOverlay({
  engine: audioEngine,
  mountEl: document.getElementById('audio-debug'),
});
debugOverlay.mount();

// One menu sheet holds every section. Hamburger button in the corner opens
// it; X in the sheet header closes. Each section is independently
// collapsible inside the sheet, persisted to localStorage.
injectSectionStyle();
const menuSheet = new MenuSheet({ mountEl: document.body, title: 'Controls' });
menuSheet.mount();

const sourcePicker = new SourcePicker({
  engine: audioEngine,
  sourceManager,
  modeSwitcher,
});
const knobPanel = new KnobPanel({ fractal, renderer });

const sourceBody = sourcePicker.buildSourceSection();
const { shape, color, audio, resolution } = knobPanel.buildSections();

menuSheet.appendSection(collapsibleSection({ key: 'source',     title: 'Source',     bodyEl: sourceBody, defaultOpen: true }));
menuSheet.appendSection(collapsibleSection({ key: 'shape',      title: 'Shape',      bodyEl: shape,      defaultOpen: true }));
menuSheet.appendSection(collapsibleSection({ key: 'color',      title: 'Color',      bodyEl: color,      defaultOpen: false }));
menuSheet.appendSection(collapsibleSection({ key: 'audio',      title: 'Audio',      bodyEl: audio,      defaultOpen: false }));
menuSheet.appendSection(collapsibleSection({ key: 'resolution', title: 'Resolution', bodyEl: resolution, defaultOpen: false }));

const adaptive = new AdaptiveScaler();
let fpsAccum = 0;
let fpsFrames = 0;
let lastFpsUpdate = 0;
let bloomRampFrame = 0;

const loop = new RenderLoop((frameMs) => {
  audioEngine.update(frameMs);
  debugOverlay.update();
  modeSwitcher.update(audioEngine.signals, performance.now() / 1000, frameMs);

  adaptive.recordFrameMs(frameMs);
  adaptive.tick();
  renderer.setScale(adaptive.scale);

  if (bloomRampFrame <= BLOOM_RAMP_FRAMES) {
    const t = bloomRampFrame / BLOOM_RAMP_FRAMES;
    const strength = BLOOM_START_STRENGTH + (BLOOM_TARGET_STRENGTH - BLOOM_START_STRENGTH) * t;
    renderer.setBloom({ strength });
    bloomRampFrame++;
  }

  renderer.render();

  fpsAccum += frameMs;
  fpsFrames += 1;
  if (performance.now() - lastFpsUpdate > 250) {
    const fps = (1000 * fpsFrames / fpsAccum).toFixed(0);
    fpsEl.textContent = `${fps} fps · ${(adaptive.scale * 100).toFixed(0)}%`;
    fpsAccum = 0; fpsFrames = 0;
    lastFpsUpdate = performance.now();
  }
});

loop.start();

// Z hotkey -> toggle browser fullscreen on the canvas's parent (document
// root, so the sheet stays accessible). On Safari the Fullscreen API
// requires user-initiated gesture, which a keydown satisfies.
window.addEventListener('keydown', (e) => {
  if (e.key === 'z' || e.key === 'Z') {
    const root = document.documentElement;
    if (!document.fullscreenElement) {
      const req = root.requestFullscreen || root.webkitRequestFullscreen;
      if (req) { req.call(root).catch(() => {}); }
    } else {
      const exit = document.exitFullscreen || document.webkitExitFullscreen;
      if (exit) { exit.call(document).catch(() => {}); }
    }
  }
});

// Fractal needs viewport resolution in pixels for aspect-correct rays.
window.addEventListener('resize', () => {
  fractal.setResolution(window.innerWidth, window.innerHeight);
});

canvas.addEventListener('webglcontextlost', (e) => {
  e.preventDefault();
  loop.stop();
  console.warn('[digable-planets] WebGL context lost');
});
canvas.addEventListener('webglcontextrestored', () => {
  console.warn('[digable-planets] WebGL context restored — reloading');
  location.reload();
});

// Tear down audio + render loop on navigation. Without this an active
// MediaStream (mic/tab) plus the AudioContext can stay open across a
// reload, leaking into the next page load. pagehide fires on reload,
// tab close, and bfcache transitions.
window.addEventListener('pagehide', () => {
  loop.stop();
  sourceManager.stop();
  knobPanel.destroy();
  menuSheet.destroy();
  modeSwitcher.dispose();
});
